"use client";

import { c as _c } from "react/compiler-runtime";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useFileUploader } from "@/hooks/use-file-uploader";
import { TFileType, TUploadedFile, ZUploadProgress } from "@/types/types";
import { cn } from "@/utils/helpers";
import { Check, Upload, X } from "lucide-react";
import { useDropzone } from "react-dropzone";
const acceptedTypes = {
  image: {
    "image/*": []
  },
  video: {
    "video/*": [],
    "video/ogg": [],
    "video/quicktime": [],
    "video/webm": [],
    "video/x-matroska": []
  }
};
type FileDropzoneProps<K extends TFileType> = {
  fileType: K;
  multiple?: boolean;
  onSelectFileAction?: (selectedItem: TUploadedFile[]) => void;
  onUploadFinishAction?: () => void;
  className?: string;
};
export default function FileDropzone(t0) {
  const $ = _c(30);
  const {
    fileType,
    multiple: t1,
    onSelectFileAction,
    onUploadFinishAction,
    className
  } = t0;
  const multiple = t1 === undefined ? true : t1;
  const {
    isUploading,
    progress,
    uploadFiles,
    cancelUpload
  } = useFileUploader(fileType);
  const t2 = acceptedTypes[fileType];
  let t3;
  if ($[0] !== multiple || $[1] !== onSelectFileAction || $[2] !== onUploadFinishAction || $[3] !== uploadFiles) {
    t3 = async files => {
      const uploadedFiles = await uploadFiles(files);
      if (onSelectFileAction && uploadedFiles.length > 0) {
        onSelectFileAction(multiple ? uploadedFiles : [uploadedFiles[0]]);
      }
      if (onUploadFinishAction) {
        onUploadFinishAction();
      }
    };
    $[0] = multiple;
    $[1] = onSelectFileAction;
    $[2] = onUploadFinishAction;
    $[3] = uploadFiles;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== multiple || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      accept: t2,
      multiple,
      onDrop: t3
    };
    $[5] = multiple;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone(t4);
  let t5;
  if ($[9] !== getRootProps) {
    t5 = getRootProps();
    $[9] = getRootProps;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = isDragActive ? "border-primary/50" : "border-muted";
  const t7 = isUploading ? "cursor-auto" : "cursor-pointer";
  let t8;
  if ($[11] !== className || $[12] !== t6 || $[13] !== t7) {
    t8 = cn("flex flex-grow items-center justify-center rounded-4xl border-2 border-dashed p-6", t6, t7, className);
    $[11] = className;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== getInputProps) {
    t9 = getInputProps();
    $[15] = getInputProps;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t9) {
    t10 = <input {...t9} />;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== cancelUpload || $[20] !== fileType || $[21] !== isUploading || $[22] !== multiple || $[23] !== progress) {
    t11 = isUploading ? <div className="h-full w-full"><h4 className="mb-4 pl-1 text-lg font-bold">Uploading {multiple ? "files" : "file"}</h4><UploadProgressList progress={progress} cancelUpload={cancelUpload} fileType={fileType} /></div> : <UploadPlaceholder fileType={fileType} multiple={multiple} />;
    $[19] = cancelUpload;
    $[20] = fileType;
    $[21] = isUploading;
    $[22] = multiple;
    $[23] = progress;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t5 || $[28] !== t8) {
    t12 = <Card {...t5} className={t8}>{t10}{t11}</Card>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t5;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}
function UploadPlaceholder(t0) {
  const $ = _c(4);
  const {
    fileType,
    multiple
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="group relative flex size-24 items-center justify-center rounded-full bg-muted p-4 transition hover:bg-primary"><Upload size={32} className="absolute text-muted-foreground group-hover:text-background" /></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = multiple && "s";
  let t3;
  if ($[1] !== fileType || $[2] !== t2) {
    t3 = <div className="flex flex-col items-center gap-4 text-center">{t1}<p className="text-sm text-muted-foreground">{"Drag & drop "}{fileType}{t2} here, or click to select</p></div>;
    $[1] = fileType;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function UploadProgressList(t0) {
  const $ = _c(9);
  const {
    progress,
    cancelUpload,
    fileType
  } = t0;
  let t1;
  if ($[0] !== cancelUpload || $[1] !== fileType || $[2] !== progress) {
    let t2;
    if ($[4] !== cancelUpload || $[5] !== fileType) {
      t2 = item => <li key={item.fileId} className="space-y-2 text-left text-sm"><span className="line-clamp-1 overflow-ellipsis">{item.fileId}</span><div className="flex items-center gap-4 text-right"><Progress value={item.percent} className="h-1" /><div className="flex items-center justify-end gap-2"><span>{item.percent === 0 || item.percent === 100 ? item.status : `${item.percent}%`}</span><button onClick={e => {
              e.stopPropagation();
              cancelUpload(item.fileId, fileType, item.fileKey);
            }} className="rounded-full bg-muted p-1">{item.percent < 100 ? <X size={12} /> : <Check size={12} />}</button></div></div></li>;
      $[4] = cancelUpload;
      $[5] = fileType;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = progress.sort(_temp).map(t2);
    $[0] = cancelUpload;
    $[1] = fileType;
    $[2] = progress;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <ul className="flex max-h-[85%] flex-col gap-2 overflow-auto rounded-xl border p-4">{t1}</ul>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function _temp(a, b) {
  return a.fileId.localeCompare(b.fileId);
}